import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SquaresFour,
  FolderNotchIcon as FolderNotch,
  GitMerge,
  WarningCircle,
  Target,
  Sparkle,
  CurrencyDollar,
  Cpu,
  Gear,
  SignOut,
  ChartLineUp,
  House,
} from "@phosphor-icons/react";
import { cn } from "../ui/utils";
import { glassOutlineClass, invertedFillClass } from "../ui/button";
import { CountBadge } from "../ui/StatusPill";
import { DarkModeToggle } from "../themes/DarkModeToggle";
import { getPgmNavPath } from "@/pages/new/pgm/pgmNavigation";

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  iconWeight?: "regular" | "fill" | "bold";
  badge?: number;
  section: "execution" | "program" | "intelligence" | "admin";
}

const navItems: NavItem[] = [
  { label: "Fluent dashboard", path: "/dashboard", icon: SquaresFour, section: "execution" },
  { label: "Workflow stages", path: "/workflow", icon: GitMerge, section: "execution" },
  { label: "Interventions", path: "/interventions", icon: WarningCircle, section: "execution" },
  { label: "Program Overview", path: "/control-tower", icon: House, section: "program" },
  { label: "Program portfolio", path: "/pgm", icon: ChartLineUp, section: "program" },
  { label: "Release workspaces", path: "/projects", icon: FolderNotch, section: "program" },
  { label: "Context Quality Analytics", path: "/context-analytics", icon: Target, section: "intelligence" },
  { label: "AI Recommendations", path: "/recommendations", icon: Sparkle, section: "intelligence" },
  { label: "AI costs", path: "/cost", icon: CurrencyDollar, section: "intelligence" },
  { label: "AI Usage & Efficiency", path: "/usage", icon: Cpu, section: "intelligence" },
];

const sectionLabels: Record<string, string> = {
  execution: "SDLC Execution",
  program: "Portfolio Management",
  intelligence: "Fluent.ai Analytics",
};

interface GlassSidebarProps {
  interventionCount?: number;
  recommendationCount?: number;
  userInitials?: string;
  userName?: string;
  workspaceName?: string;
  onSignOut?: () => void;
}

export function GlassSidebar({
  interventionCount = 0,
  recommendationCount = 0,
  userInitials = "AU",
  userName = "Admin User",
  workspaceName = "Fluent.ai Workspace",
  onSignOut,
}: GlassSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [logoError, setLogoError] = React.useState(false);

  const isActive = (path: string) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    if (path === "/admin") return location.pathname.startsWith("/admin");
    return location.pathname.startsWith(path);
  };

  const getBadge = (item: NavItem) => {
    if (item.path === "/interventions" && interventionCount > 0) return interventionCount;
    if (item.path === "/recommendations" && recommendationCount > 0) return recommendationCount;
    return undefined;
  };

  const sections = ["program", "execution", "intelligence"] as const;

  return (
    <aside className="w-[260px] glass-sidebar flex flex-col justify-between h-screen z-50 shrink-0">
      <div>
        {/* Fluent.ai logo symbol </> — light and dark assets per theme */}
        <div className="h-14 flex items-center px-6 opacity-90 hover:opacity-100 transition-opacity" aria-hidden>
          {!logoError ? (
            <div className="relative h-8 min-w-[2rem] flex items-center">
              <img
                src="/assets/fluent-logo-symbol.png"
                alt="Fluent.ai"
                className="h-8 w-auto object-contain dark:opacity-0 dark:invisible absolute left-0"
                onError={() => setLogoError(true)}
              />
              <img
                src="/assets/fluent-logo-symbol_dark.png"
                alt="Fluent.ai"
                className="h-8 w-auto object-contain opacity-0 invisible dark:opacity-100 dark:visible absolute left-0"
                onError={() => setLogoError(true)}
              />
            </div>
          ) : (
            <span className="text-xl font-mono font-semibold text-slate-400 dark:text-slate-300">
              {"</>"}
            </span>
          )}
        </div>

        {/* User Card */}
        <div className="px-4 mb-6">
          <div className="flex items-center gap-3 p-3 bg-white/40 rounded-2xl border border-white/40 shadow-sm cursor-pointer hover:bg-white/60 transition-all dark:bg-slate-800/40 dark:border-slate-700/40 dark:hover:bg-slate-800/60">
            <div className="w-10 h-10 rounded-full bg-gradient-to-b from-slate-200 to-slate-300 flex items-center justify-center text-slate-500 font-bold shadow-inner text-sm dark:from-slate-600 dark:to-slate-700 dark:text-slate-300">
              {userInitials}
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">{userName}</div>
              <div className="text-[11px] text-slate-500 font-medium dark:text-slate-400">{workspaceName}</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="px-4 space-y-1">
          {sections.map((section) => (
            <React.Fragment key={section}>
              <div className="px-3 text-[13px] font-bold text-slate-700 dark:text-slate-200 tracking-wide mb-1.5 mt-4 first:mt-0">
                {sectionLabels[section]}
              </div>
              {navItems
                .filter((item) => item.section === section)
                .map((item) => {
                  const active = isActive(item.path);
                  const Icon = item.icon;
                  const badge = getBadge(item);

                  return (
                    <button
                      key={item.path}
                      type="button"
                      onClick={() => navigate(item.path === "/pgm" ? getPgmNavPath() : item.path)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-xl transition-all",
                        active
                          ? invertedFillClass
                          : "text-slate-600 hover:bg-black/5 dark:text-slate-400 dark:hover:bg-white/5"
                      )}
                    >
                      <Icon
                        size={20}
                        weight={active ? "fill" : "regular"}
                      />
                      <span>{item.label}</span>
                      {badge !== undefined && (
                        <CountBadge className="ml-auto">{badge}</CountBadge>
                      )}
                    </button>
                  );
                })}
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* Footer: Theme toggle + Settings + Sign Out */}
      <div className="p-4 space-y-2">
        <DarkModeToggle className="w-full [&_button]:w-full [&_button]:justify-start [&_button]:rounded-xl [&_button]:text-xs [&_button]:font-semibold" showLabel={true} />
        <button
          type="button"
          onClick={() => navigate("/admin")}
          className={cn(
            "w-full flex items-center gap-2 p-2.5 rounded-xl text-xs font-semibold transition-all",
            isActive("/admin")
              ? invertedFillClass
              : glassOutlineClass
          )}
        >
          <Gear size={20} weight={isActive("/admin") ? "fill" : "regular"} />
          <span>Settings</span>
        </button>
        {onSignOut && (
          <button
            type="button"
            onClick={onSignOut}
            className="w-full flex items-center gap-2 p-2.5 rounded-xl text-xs font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
          >
            <SignOut size={20} weight="regular" />
            <span>Sign Out</span>
          </button>
        )}
      </div>
    </aside>
  );
}
